<template>
    <SearchCardLayout class="people-card" :disabled="!userPermissionConfig.canOpenPeopleInsight">
        <template #left>
            <div class="avatar">
                <TessAvatar class="arbo-ui-styles" v-bind="avatarTemp" />
            </div>
        </template>
        <template #quick-action>
            <ArboButton
                v-if="userPermissionConfig.canOpenPeopleInsight"
                class="arbo-ui-styles"
                :label="'Insights'"
                :prefix-icon="'IdCardClip'"
                size="small"
                @click.stop="openPeopleInsight" />
        </template>
        <template #more-actions="{ show, hide }">
            <TessMoreActions
                v-bind="userMoreActionComputed"
                class="arbo-ui-styles"
                size="small"
                @show="show"
                @hide="hide" />
        </template>
        <template #detail>
            <div class="information">
                <div class="user-name">{{ props.data?.display_name }}</div>
                <div class="user-email">{{ props.data?.user?.email }}</div>
            </div>
        </template>
    </SearchCardLayout>
</template>
<script setup lang="ts">
import SearchCardLayout from './CardLayout.vue';
import type { PeopleCardData } from 'src/types';
import { createCommonLink } from '@arbocollab/arbo-ui-components';
import { computed, inject, ref } from 'vue';
import { baseLinkConfig, clickActionHandler } from 'src/plugins';
import { storeToRefs } from 'pinia';
import { useUserStore } from 'src/stores/userStore';
type Props = {
    data: PeopleCardData;
};
const props = defineProps<Props>();
const currentUserId = ref('');
const avatarTemp = computed(() => ({
    label: '',
    labelTooltip: '',
    icon: '',
    image: props.data?.profile_image,
    isMe: props.data?.user_id === currentUserId.value,
    disableHover: true,
    bgColor: props.data?.color?.bgColor,
    textColor: props.data?.color?.textColor,
    isOpenNewTab: false,
    isUseShortLabel: false,
    shortLabel: props.data?.initial,
    name: props.data?.display_name
}));

const copyFunc = (e: MouseEvent) => {
    const copyText = createCommonLink({
        type: 'people_insight',
        config: {
            ...baseLinkConfig(),
            user_id: props.data?.id
        }
    }).url;
    navigator.clipboard
        .writeText(copyText)
        .then(() => {
            // console.warn('Email copied to clipboard');
        })
        .catch((err) => {
            console.error('Failed to copy: ', err, e);
        });
};
const switchInstance = inject('switchInstance') as any;
const openPeopleDetails = (e: MouseEvent) => {
    const user_id = props.data?.id;
    const display_name = props.data?.display_name;
    const url = createCommonLink({
        type: 'people_detail',
        config: {
            ...baseLinkConfig(),
            user_id: user_id,
            display_name: display_name
        }
    }).url;
    const _switchInstance = switchInstance('people_insight', { tab: '', id: user_id || '' }, { showLoading: false });
    clickActionHandler(e, url, {}, _switchInstance);
};
const openPeopleInsight = (e: MouseEvent) => {
    const url = createCommonLink({
        type: 'people_insight',
        config: {
            ...baseLinkConfig(),
            user_id: props.data?.id
        }
    }).url;
    if (e.metaKey || e.ctrlKey) {
        window.open(url, '_blank');
        return;
    }
    if (switchInstance) {
        switchInstance('people_insight', { tab: 'home', id: props.data?.id || '' }, { showLoading: false });

        return;
    }

    window.open(url, '_blank');
};
const userStore = useUserStore();
const { userPermissionConfig } = storeToRefs(userStore);
const userMoreActionComputed = computed(() => {
    const items = [
        {
            id: '4',
            label: 'Copy',
            icon: 'Link',
            visible: true,
            type: 'action',

            action: copyFunc
        }
    ];
    if (userPermissionConfig.value.canOpenPeopleDetail) {
        items.push({
            id: '7',
            label: 'People details',
            icon: 'User',
            action: openPeopleDetails,
            visible: userPermissionConfig.value.canOpenPeopleDetail,
            type: 'action'
        });
    }
    return {
        menuItems: [
            {
                items: items
            }
        ],
        tooltip: 'More actions',
        icon: 'EllipsisVertical'
    };
});
</script>
